import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  usePaginatedAssetVendors,
  useDeleteAssetVendor,
  useBulkDeleteAssetVendors,
} from '../../../services/httpServices/queries';
import { useDebounce } from '../../../hooks/useDebounce';
import { Pagination } from '../../../components/ui/Pagination';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { ExpensesLayout } from '../expenses/ExpensesLayout';
import { AssetVendorDetailsModal } from './AssetVendorDetailsModal';
import { AssetVendorCsvImportModal } from './AssetVendorCsvImportModal';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  X,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  Upload,
} from 'lucide-react';
import type { AssetVendor } from '../../../types/asset';

export function AssetVendors() {
  const limit = 20;
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [confirm, setConfirm] = useState<AssetVendor | null>(null);
  const [viewing, setViewing] = useState<AssetVendor | null>(null);
  const [csvOpen, setCsvOpen] = useState(false);

  const { data, isLoading, error } = usePaginatedAssetVendors(page, limit, debouncedSearch || undefined);
  const vendors = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;
  const deleteVendor = useDeleteAssetVendor();
  const bulkDeleteVendors = useBulkDeleteAssetVendors();

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(vendors.map((vendor) => vendor.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [vendors]);

  const allSelected = vendors.length > 0 && vendors.every((vendor) => selectedIds.has(vendor.id));
  const someSelected = vendors.some((vendor) => selectedIds.has(vendor.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // If the current page becomes empty (e.g. after deleting its rows), step back.
  useEffect(() => {
    if (!isLoading && vendors.length === 0 && page > 1) {
      setPage((prev) => Math.max(1, prev - 1));
    }
  }, [isLoading, vendors.length, page]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = vendors.length > 0 && vendors.every((vendor) => prev.has(vendor.id));
      return everySelected ? new Set() : new Set(vendors.map((vendor) => vendor.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openEdit = (vendor: AssetVendor) => {
    setViewing(null);
    navigate(`/billing/expenses/vendors/${vendor.id}/edit`);
  };

  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await deleteVendor.mutateAsync(confirm.id);
      toast.success('Vendor deleted');
    } catch {
      toast.error('Failed to delete vendor');
    } finally {
      setConfirm(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteVendors.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} vendor${ids.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete some vendors');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  return (
    <ExpensesLayout
      title="Vendors"
      description="Suppliers for expenses and asset purchases"
      helpKey="/billing/expenses/vendors"
      action={
        <>
          <button
            type="button"
            onClick={() => setCsvOpen(true)}
            className="px-4 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Upload size={16} />
            Import CSV
          </button>
          <Link
            to="/billing/expenses/vendors/new"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Add Vendor
          </Link>
        </>
      }
    >
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendors..."
            className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteVendors.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteVendors.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteVendors.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="px-4 py-3 w-10 text-left">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={vendors.length === 0}
                    title="Select all"
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Terms</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-danger">
                    <AlertCircle size={18} className="inline mr-2" /> Failed to load vendors
                  </td>
                </tr>
              ) : vendors.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-text-muted">
                    {search ? (
                      <span>
                        No vendors match your search.{' '}
                        <button onClick={() => setSearch('')} className="text-primary hover:underline">
                          Clear search
                        </button>
                      </span>
                    ) : (
                      'No vendors found. Add your first vendor to get started.'
                    )}
                  </td>
                </tr>
              ) : (
                vendors.map((vendor) => (
                  <tr key={vendor.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(vendor.id)}
                        onChange={() => toggleOne(vendor.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setViewing(vendor)}
                        className="font-medium text-text hover:text-primary transition-colors text-left"
                        title="View details"
                      >
                        {vendor.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-mono text-text-muted">{vendor.vendorCode}</td>
                    <td className="px-4 py-3 text-text-muted">{vendor.contactPerson || '—'}</td>
                    <td className="px-4 py-3 text-text-muted">{vendor.phoneNumber || '—'}</td>
                    <td className="px-4 py-3 text-text-muted">
                      {[vendor.location, vendor.city].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-text-muted">{vendor.paymentTermsDays} days</td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          vendor.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                        }`}
                      >
                        {vendor.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        {vendor.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/billing/expenses/vendors/${vendor.id}/edit`}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </Link>
                        <button
                          type="button"
                          onClick={() => setConfirm(vendor)}
                          disabled={deleteVendor.isPending}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="vendors"
        />
      </div>

      <AssetVendorDetailsModal
        open={viewing !== null}
        vendor={viewing}
        onClose={() => setViewing(null)}
        onEdit={openEdit}
      />

      <AssetVendorCsvImportModal
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        onImported={() => {
          setSearch('');
          setPage(1);
        }}
      />

      <ConfirmDialog
        open={confirm !== null}
        title="Delete vendor"
        description={
          <>
            Delete <strong>{confirm?.name}</strong>? This cannot be undone.
          </>
        }
        loading={deleteVendor.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} vendor${selectedIds.size === 1 ? '' : 's'}?`}
        description="This will remove the selected vendors. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} vendor${selectedIds.size === 1 ? '' : 's'}`}
        loading={bulkDeleteVendors.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </ExpensesLayout>
  );
}
